/**
 * "Grid settings" control for the customer map's structure list.
 *
 * It is the grids' own control and the grids' own dialog: the same Tune button
 * and the same drag-to-reorder, uncheck-to-hide list, so arranging what a map
 * row says is the same gesture as arranging a grid's columns. The map has no
 * stat cards, so the dialog renders the columns section alone.
 *
 * Saving writes the choice straight to the user's odl-app preference — the key
 * the legacy plant map already reads, holding the shown field ids in display
 * order — so a saved arrangement carries over between the two maps.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import GridConfigureDialog from '@sophocles/data-grids/components/GridConfigureDialog';
import { GridSettingsButton } from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';
import { CONFIGURABLE_FIELDS, fieldLabel } from '@sophocles/views/map/mapFields';

/**
 * The catalog in the order the rows render it: the shown fields first, in the
 * order they were saved, then everything still on offer.
 *
 * @param {string[]} shownFields
 * @param {(field: object) => string} labelOf
 * @returns {{ items: object[], visibilityModel: Record<string, boolean> }}
 */
function buildColumnsSection(shownFields, labelOf) {
  const byId = new Map(CONFIGURABLE_FIELDS.map(field => [field.id, field]));
  const ordered = [
    ...shownFields.filter(fieldId => byId.has(fieldId)).map(fieldId => byId.get(fieldId)),
    ...CONFIGURABLE_FIELDS.filter(field => !shownFields.includes(field.id)),
  ];
  return {
    items: ordered.map(field => ({ id: field.id, label: labelOf(field) })),
    visibilityModel: Object.fromEntries(CONFIGURABLE_FIELDS.map(field => [field.id, shownFields.includes(field.id)])),
  };
}

/**
 * @param {object} props
 * @param {string[]} props.shownFields - field ids currently shown on each row.
 * @param {(fields: string[]) => void} props.onShownFieldsChange - persist a new selection.
 */
export default function MapConfigureListButton({ shownFields, onShownFieldsChange }) {
  const { t } = useTranslation();
  const relayLoadTerm = useRelayLoadTerm();
  const [isOpen, setIsOpen] = useState(false);

  const columns = useMemo(
    () =>
      buildColumnsSection(shownFields, field => fieldLabel(field, { t, relayLoadTermPlural: relayLoadTerm.plural })),
    [shownFields, t, relayLoadTerm.plural],
  );

  // The dialog speaks order + visibility; the preference holds the shown ids in
  // display order, which is that pair flattened.
  const handleSave = ({ columns: saved }) => {
    if (!saved) return;
    onShownFieldsChange(saved.order.filter(fieldId => saved.visibilityModel[fieldId] !== false));
  };

  return (
    <>
      <GridSettingsButton tooltip={t('general.gridSettings', 'Grid settings')} onClick={() => setIsOpen(true)} />
      {isOpen && (
        <GridConfigureDialog
          open
          onClose={() => setIsOpen(false)}
          columns={columns}
          statCards={null}
          onSave={handleSave}
        />
      )}
    </>
  );
}
